import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PromoRedeem({ onRedeemed, onDiscount }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const redeem = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: code.trim() });
      if (data.kind === "discount") {
        toast.success(data.message || "Discount applied at checkout.");
        setCode("");
        if (onDiscount) onDiscount(data);
        else navigate(`/paywall?code=${encodeURIComponent(data.code)}`);
        return;
      }
      setUser((u) => ({ ...(u || {}), is_premium: true }));
      toast.success(data.message || "Promo code applied — you're all set.");
      setCode("");
      onRedeemed?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code couldn't be applied. Please check it and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="promo-redeem" className="rounded-2xl border border-[#E5DED4] bg-[#F7F2EA] p-5">
      <p className="text-sm font-medium text-[#291B2D] flex items-center gap-2">
        <Ticket className="h-4 w-4 text-[#C89B55]" /> Have a promo code?
      </p>
      <div className="mt-3 flex gap-2">
        <input
          data-testid="promo-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
          placeholder="Enter code"
          className="flex-1 bg-white border border-[#E5DED4] rounded-full px-4 py-2.5 text-sm tracking-wide uppercase focus:outline-none focus:ring-2 focus:ring-[#C89B55]"
        />
        <button
          data-testid="promo-redeem-button"
          onClick={redeem}
          disabled={busy || !code.trim()}
          className="rounded-full bg-[#291B2D] text-[#FCFAF6] px-6 py-2.5 text-sm font-medium hover:bg-[#352238] disabled:opacity-40 transition-all flex items-center gap-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Apply
        </button>
      </div>
    </div>
  );
}
